import type { DocumentToolRef } from '@captain-tasko/shared';

/**
 * CTSK-365 — "Source for: {tool}" chips on the document detail page.
 *
 * Rendered when the document is one of an installed tool's registry
 * files (the API's `referencedByTools[]`, derived from tool manifests
 * at read time — see GET /documents/:id). Clicking a chip navigates
 * (same tab, SPA) to the tool's page at /tools/{slug}.
 *
 * Empty / absent list renders NOTHING — documents that aren't a
 * system-of-record for any tool get no dead affordance, per the
 * ticket's AC 4. One chip per referencing tool (AC 5).
 *
 * Styling sits with the document badge family (container chip:
 * squarish radius, soft tint, 12-13px) but stays subdued — border +
 * muted text, no color signature, since the chip is informational
 * provenance rather than a category.
 */
export function SourceForToolChips({
  tools,
  onNavigate,
}: {
  tools?: DocumentToolRef[] | null;
  onNavigate: (path: string) => void;
}) {
  if (!tools || tools.length === 0) return null;
  return (
    <>
      {tools.map((t) => (
        <a
          key={t.slug}
          href={`/tools/${t.slug}`}
          onClick={(e) => {
            e.preventDefault();
            onNavigate(`/tools/${t.slug}`);
          }}
          title={
            t.filename
              ? `This document is the system of record (${t.filename}) for ${t.name} — open the tool`
              : `This document is a system of record for ${t.name} — open the tool`
          }
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '3px 10px',
            border: '1px solid var(--border-strong)',
            background: 'var(--surface-sunken)',
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 500,
            color: 'var(--text-secondary)',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          <span aria-hidden style={{ fontSize: 11 }}>⚙</span>
          Source for: {t.name}
        </a>
      ))}
    </>
  );
}
